import type { PlayerColor } from '@/shared/board/types'
import { allowedMoves, without } from '@/shared/engine/resolver'
import { applyMove, type GameState } from '@/shared/engine/state'
import type { Move } from '@/shared/game/types'

/*
 * Что компьютер вообще может сделать этим броском — общее для любой головы:
 * сеть оценивает эти замыслы, прежняя эвристика (tools/heuristic) — тоже.
 * Решение принимается сразу на оба кубика: перебираются пары ходов, а не
 * лучший ход по одному.
 */

/**
 * Замыслы на этот бросок: пары ходов, если играются оба кубика, и одиночные
 * ходы, если второй сыграть нечем.
 *
 * Вместе они не смешиваются: пара всегда «длиннее» и по сумме оценок
 * побеждала бы любой одиночный ход, даже когда сравнивать их не за что.
 */
export function plansFor(state: GameState, color: PlayerColor, dice: number[]): Move[][] {
  const first = allowedMoves(state, color, dice)

  if (first.length === 0) return []

  const pairs: Move[][] = []

  for (const move of first) {
    const rest = without(dice, move.die)

    if (rest.length === 0) continue

    for (const second of allowedMoves(applyMove(state, move), color, rest)) {
      pairs.push([move, second])
    }
  }

  return pairs.length > 0 ? pairs : first.map((move) => [move])
}

/**
 * Что из замысла уходит одним действием.
 *
 * Пара одной фишкой — целиком: это ход «на сумму», и на доске он выглядит
 * одним движением. Пара разными фишками — только первым ходом: второй бот
 * выберет заново, зато обе фишки будут видны, как они идут.
 */
export function sendable(plan: Move[]): Move[] {
  if (plan.length === 2 && plan[0].token !== plan[1].token) return [plan[0]]

  return plan
}
